// Doc Order — state hook.
// Owns the aggregate (order + fees + derivedDocs + invoiceFolderStatus + viewer)
// and the autosave engine: edits update local state optimistically, mark their
// section/fee dirty, and a ~900ms debounce flushes them to `save-draft` ONE AT A
// TIME (each carries the current revision; each bumps it). A server response is
// reconciled so still-pending optimistic edits survive it; a 409 reloads and a
// 422 keeps the local edit with a toast. All business rules stay in the edge
// function + core — this hook only plumbs state.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  docOrderService,
  type DocOrderAggregate,
  type DocOrderCapacity,
  type DocOrderEmailClaim,
  type DocOrderFolderDocs,
  type DocOrderLoProfile,
  type DocOrderLoSummaryPayload,
  type DocOrderRow,
  type DocOrderSettlement,
  type DocOrderResponse,
  type DocOrderUploadTarget,
  type DocOrderWritebacks,
  type SaveDraftSection
} from '@/services/docOrderService';
import type { ChecklistDoc, DocOrderFee, DocOrderViewer, InsuranceInfo, VestingWorkflowState } from './core';

const EMPTY_WRITEBACKS: DocOrderWritebacks = {
  enabled: false,
  reason: '',
  pendingFields: 0,
  lenderCreditWriteStatus: null
};

const AUTOSAVE_DEBOUNCE_MS = 900;

/** Round-5 (Codex R5-a): ok + aggregateUnavailable = the mutation APPLIED but
 *  the response aggregate raced a concurrent writer — success that still
 *  needs a converging refetch, NEVER a failure and NEVER a replay. */
function appliedUnconverged(res: { ok: boolean; aggregate?: unknown; aggregateUnavailable?: boolean }): boolean {
  return res.ok === true && !res.aggregate && res.aggregateUnavailable === true;
}

/** Outcome of one autosave drain (R5 review-5 #1): `drained` = queues empty;
 *  `rejected` = a 422 soft-drop happened (server refused a value); `stalled`
 *  = a hard reject / retry exhaustion left entries queued. */
interface DrainResult {
  drained: boolean;
  rejected: boolean;
  stalled: boolean;
  /** Round-6 #1: an APPLIED save's convergence refetch failed — the local
   *  revision is stale. Nothing was lost, but no transition may proceed. */
  convergeFailed: boolean;
}

const CLOSING_COLUMNS = [
  'signing_date',
  'closing_date',
  'first_payment_date',
  'power_of_attorney',
  'sign_type',
  'doc_email',
  'doc_dating'
] as const;

export interface VestingPatch {
  vesting?: string | null;
  vesting_workflow?: Partial<VestingWorkflowState>;
  vesting_confirmed?: boolean;
}

export interface UseDocOrderResult {
  loading: boolean;
  error: string | null;
  order: DocOrderRow | null;
  fees: DocOrderFee[];
  derivedDocs: ChecklistDoc[];
  invoiceFolderStatus: Record<string, boolean>;
  invoiceFolderDocs: Record<string, DocOrderFolderDocs>;
  viewer: DocOrderViewer | null;
  snapshotStale: boolean;
  hasOrder: boolean;
  creating: boolean;
  createRetryable: boolean;
  openRetryable: boolean;
  /** Round-5 #3: an applied mutation could not converge — the workspace is
   *  provably stale. Mutations refuse until reopen() succeeds; Workspace
   *  renders the blocking Reload banner off this. */
  needsReload: boolean;
  /** Dirty-PRESERVING converge retry for the reload banner (round-6 #2b). */
  retryReload: () => Promise<void>;
  refreshing: boolean;
  signing: boolean;
  saving: boolean;
  /** Autosave retried to exhaustion and gave up WITHOUT losing the edits —
   *  the dirty state is intact; call flush() to retry. Drives an "unsaved
   *  changes" banner (#14). */
  saveStalled: boolean;
  busy: boolean;
  // ---- P2+P3 aggregate reads ----
  capacity: DocOrderCapacity | null;
  loProfile: DocOrderLoProfile | null;
  loSummaryLoading: boolean;
  loSummaryError: string | null;
  loadLoSummary: () => Promise<boolean>;
  emailClaims: DocOrderEmailClaim[];
  writebacks: DocOrderWritebacks;
  createOrder: () => Promise<void>;
  refresh: () => Promise<void>;
  reopen: () => Promise<void>;
  /** Lightweight upload-status convergence (Batch 3.1 F4) — never `refresh`. */
  checkUploadStatus: () => Promise<void>;
  /** Shared in-flight state — disables every Check-status button. */
  checkingUploads: boolean;
  sign: () => Promise<{ ok: boolean; blockers?: string[]; error?: string }>;
  /** Drain pending autosaves NOW. R5 (review-5 #1): TRUE only when the drain
   *  fully completed with NO rejection and NO stall — a soft-dropped 422
   *  empties the queue but must not read as success (sign/submit/send/
   *  refresh/download/uploads all gate on this). */
  flush: () => Promise<boolean>;
  /** Awaited single-fee decision save (R5 #7b — kills the 422 false-success).
   *  The patch is built from the CURRENT fee AFTER the preliminary flush (and
   *  rebuilt after a conflict refetch) — a pre-conflict financial patch is
   *  never replayed. Fails closed: {ok:false} carries status/code/error. */
  saveFeeNow: (
    feeId: string,
    buildPatch: (fee: DocOrderFee) => Partial<DocOrderFee>
  ) => Promise<{ ok: boolean; status?: number; code?: string; error?: string }>;
  escalateFee: (feeId: string, note: string) => Promise<DocOrderResponse>;
  undoEscalation: (feeId: string) => Promise<DocOrderResponse>;
  setFirstPaymentOverride: (input: { month: string; reason: string } | { clear: true }) => Promise<DocOrderResponse>;
  patchClosing: (patch: Partial<Pick<DocOrderRow, (typeof CLOSING_COLUMNS)[number]>>) => void;
  patchSettlement: (patch: Partial<DocOrderSettlement>) => void;
  patchInsurance: (patch: Partial<InsuranceInfo>) => void;
  patchVesting: (patch: VestingPatch) => void;
  saveFee: (feeId: string, patch: Partial<DocOrderFee>) => void;
  addManualFee: (patch: { name: string; payee?: string | null; amount?: number }) => Promise<void>;
  // ---- P2+P3 workflow actions (each returns the raw response for UI branching) ----
  deferInsurance: (reason: string) => Promise<DocOrderResponse>;
  submit: () => Promise<DocOrderResponse>;
  returnToProcessor: () => Promise<DocOrderResponse>;
  /** R7 — reopen a SIGNED (complete) order back to Closing (reason required).
   *  NB: distinct from reopen(), which is a re-FETCH of the current loan. */
  reopenOrder: (reason: string) => Promise<DocOrderResponse>;
  closingEditFee: (feeId: string, patch: Partial<DocOrderFee>) => Promise<DocOrderResponse>;
  approveFee: (feeId: string, approve: boolean) => Promise<DocOrderResponse>;
  vestingAttest: () => Promise<DocOrderResponse>;
  resolveChangeRequest: () => Promise<DocOrderResponse>;
  sendToLo: () => Promise<DocOrderResponse>;
  setLenderCredit: (amount: number) => Promise<DocOrderResponse>;
  escalateLenderCredit: (amount: number) => Promise<DocOrderResponse>;
  withdrawLenderCreditEscalation: () => Promise<DocOrderResponse>;
  loRequestChanges: (
    note: string,
    items: Array<{ id: string; toAmount: number; toReimburse: boolean }>
  ) => Promise<DocOrderResponse>;
  loAcknowledge: () => Promise<DocOrderResponse>;
  retryWrites: () => Promise<DocOrderResponse>;
  resendEmail: (claimId: string) => Promise<DocOrderResponse>;
  downloadPdf: () => Promise<void>;
  // ---- P4 uploads / mark-current / reset ----
  uploadFile: (target: DocOrderUploadTarget, file: File, fileName: string) => Promise<DocOrderResponse>;
  commitMarks: (feeId: string) => Promise<DocOrderResponse>;
  resetDocument: (checklistId: string) => Promise<DocOrderResponse>;
  deleteFee: (feeId: string) => Promise<DocOrderResponse>;
}

export function useDocOrder(
  loanId: string | null | undefined,
  enabled: boolean,
  addToast: (message: string, icon?: string) => void
): UseDocOrderResult {
  const [agg, setAggState] = useState<DocOrderAggregate | null>(null);
  // Round-10 #1: the mounted-loan truth. Late mutation responses from a
  // PREVIOUS loan must never apply state, trigger convergence, or steal the
  // open sequence after the hook has moved to another loan.
  const loanIdRef = useRef<string | null | undefined>(loanId);
  const priorLoanIdRef = useRef<string | null | undefined>(loanId);
  const loanGenerationRef = useRef(0);
  if (priorLoanIdRef.current !== loanId) {
    priorLoanIdRef.current = loanId;
    loanGenerationRef.current += 1;
  }
  loanIdRef.current = loanId;
  const [viewer, setViewer] = useState<DocOrderViewer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createRetryable, setCreateRetryable] = useState(false);
  const [openRetryable, setOpenRetryable] = useState(false);
  // Round-5 #3: an APPLIED mutation whose convergence reopen ALSO failed —
  // the workspace is provably stale; block further mutations until a reload
  // succeeds (stale controls must never stay actionable past a transition).
  const [needsReload, setNeedsReloadState] = useState(false);
  const needsReloadRef = useRef(false);
  const setNeedsReload = useCallback((v: boolean) => {
    needsReloadRef.current = v;
    setNeedsReloadState(v);
  }, []);
  const [refreshing, setRefreshing] = useState(false);
  const [signing, setSigning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStalled, setSaveStalled] = useState(false);
  // Single in-flight guard for every P2+P3 workflow transition (submit, send,
  // ack, closing edits, …) so the action bar / buttons disable while one runs.
  const [busy, setBusy] = useState(false);
  const [loSummary, setLoSummaryState] = useState<DocOrderLoSummaryPayload | null>(null);
  const loSummaryRef = useRef<DocOrderLoSummaryPayload | null>(null);
  const [loSummaryLoading, setLoSummaryLoading] = useState(false);
  const [loSummaryError, setLoSummaryError] = useState<string | null>(null);
  const loSummaryInFlight = useRef<{ key: string; promise: Promise<boolean> } | null>(null);
  // One shared in-flight state for the Check-status affordance (Batch 3.1 F4)
  // — every Check-status button disables while a check runs.
  const [checkingUploads, setCheckingUploads] = useState(false);
  const checkingUploadsRef = useRef(false);

  const aggRef = useRef<DocOrderAggregate | null>(null);
  const dirtySections = useRef<Set<SaveDraftSection>>(new Set());
  const dirtyFees = useRef<Map<string, Partial<DocOrderFee>>>(new Map());
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushInFlight = useRef<{ generation: number; token: symbol; promise: Promise<DrainResult> } | null>(null);
  const feeNowInFlight = useRef<number | null>(null);
  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;
  // Monotonic open token: a late-resolving open from a previous loan must
  // never overwrite the current loan's state (round-9 finding 3; the repo's
  // standing "bump request-id refs in every branch" rule).
  const openSeq = useRef(0);

  const setAgg = useCallback((next: DocOrderAggregate | null) => {
    aggRef.current = next;
    setAggState(next);
    if (next) {
setViewer(next.viewer);
}
  }, []);

  // Merge a server aggregate while preserving edits still queued locally — a
  // save for one section must not clobber another section's unsent optimistic
  // edit (or a re-edit of the same row made while its save was in flight).
  const reconcile = useCallback((server: DocOrderAggregate) => {
    // Round-10 #1: a late response from loan A must not replace loan B's
    // internal aggregate (the exposure filter would then blank B).
    if (loanIdRef.current && (server.order.loan_id as string) !== loanIdRef.current) {
      return;
    }
    const local = aggRef.current;
    let order = server.order;
    let fees = server.fees;
    if (local) {
      for (const section of dirtySections.current) {
        order = applyLocalSection(order, local.order, section);
      }
    }
    if (dirtyFees.current.size) {
      fees = fees.map((f) => {
        const patch = dirtyFees.current.get(f.id);
        return patch ? { ...f, ...patch } : f;
      });
    }
    setAgg({ ...server, order, fees });
  }, [setAgg]);

  const applyResponse = useCallback(
    (res: DocOrderResponse, kind: 'section' | 'fee'): 'ok' | 'applied' | 'conflict' | 'soft' | 'hard' | 'resync' => {
      if (res.ok && res.aggregate) {
        reconcile(res.aggregate);
        return 'ok';
      }
      if (appliedUnconverged(res)) {
        // Round-5 #1: the autosave PERSISTED — replaying it would duplicate
        // events/revisions and could toast a false "Save failed". The drain
        // converges (dirty-preserving) and moves on; NEVER a requeue.
        return 'applied';
      }
      // R4 #3 — HARD rejects: the server said "you may not do this" (403; a 409
      // that is NOT a revision conflict, e.g. a stage gate; a SECTION 422).
      // Retrying is pointless and refetch-looping was what silently ate closer
      // edits: requeue, stop the drain, keep the optimistic edit, and surface
      // the server's own message. A FEE 422 stays a per-value 'soft' rejection
      // (dropping just that entry — server truth replaces it on refetch).
      if (res.status === 403 || (res.status === 409 && res.code !== 'revision_conflict')) {
        addToastRef.current(res.error || 'The server rejected that change', 'alertTriangle');
        return 'hard';
      }
      if (res.status === 409) {
return 'conflict';
}
      if (res.status === 422) {
        addToastRef.current(res.error || 'That change was rejected', 'alertTriangle');
        return kind === 'section' ? 'hard' : 'soft';
      }
      addToastRef.current(res.error || 'Save failed', 'alertTriangle');
      return 'resync';
    },
    [reconcile]
  );

  const doOpen = useCallback(
    async (id: string): Promise<'ok' | 'superseded' | 'failed'> => {
      // Round-11 #1: identity is enforced HERE, centrally — any late caller
      // still holding a LEFT loan's id is refused before the seq bump, so it
      // can neither install stale state nor invalidate the mounted loan's
      // in-flight open.
      if (loanIdRef.current !== id) {
        return 'superseded';
      }
      const generation = loanGenerationRef.current;
      const seq = ++openSeq.current;
      const res = await docOrderService.open(id);
      if (generation !== loanGenerationRef.current || loanIdRef.current !== id || seq !== openSeq.current) {
        // Round-6 #4: a superseded request may NOT claim success — only the
        // request that actually installs state clears reload-required.
        return 'superseded';
      }
      if (res.ok) {
        setError(null);
        setOpenRetryable(false);
        setNeedsReload(false);
        if (res.aggregate) {
setAgg(res.aggregate);
} else {
          setAgg(null);
          setViewer(res.viewer);
        }
      } else {
        setError(res.error || 'Failed to load doc order');
        // Round-3 #3: a retryable open failure (e.g. aggregate_reload_failed —
        // the order changed mid-load and the re-read raced) gets a Try-again
        // that re-opens; non-retryable errors stay terminal.
        setOpenRetryable(!!res.retryable);
        setViewer(res.viewer);
      }
      return res.ok ? 'ok' : 'failed';
    },
    [setAgg, setNeedsReload]
  );

  // Refetch the server aggregate and RECONCILE it (preserve edits still queued
  // locally), instead of doOpen's bare setAgg. This is the conflict-recovery
  // refresh: a 409 means an external write (an upload, a marks-commit, a parallel
  // edit) bumped the revision between our read and our save — reloading fresh
  // while KEEPING dirty lets the next drain iteration re-send against the new
  // revision without losing the in-flight edit.
  const refetchAndReconcile = useCallback(
    async (id: string): Promise<'ok' | 'superseded' | 'failed'> => {
      if (loanIdRef.current !== id) {
        return 'superseded'; // round-11 #1: central cross-loan refusal
      }
      const generation = loanGenerationRef.current;
      const seq = ++openSeq.current;
      const res = await docOrderService.open(id);
      if (generation !== loanGenerationRef.current || loanIdRef.current !== id || seq !== openSeq.current) {
        return 'superseded'; // round-6 #4: only the owning request clears
      }
      if (res.ok && res.aggregate) {
        reconcile(res.aggregate);
        setNeedsReload(false);
      } else if (res.ok) {
        setAgg(null);
        setViewer(res.viewer);
      } else {
        setError(res.error || 'Failed to load doc order');
        setViewer(res.viewer);
      }
      return res.ok ? 'ok' : 'failed';
    },
    [reconcile, setAgg, setNeedsReload]
  );

  // Round-5 (Codex R5-a #1–#3), THE central degraded-success contract:
  // ok + aggregateUnavailable = the mutation APPLIED but the response
  // aggregate raced a concurrent writer. ONE convergence path everywhere —
  // a dirty-preserving refetch; if THAT fails too, flip the blocking
  // needs-reload state (visible banner; all further mutations refuse until a
  // reload succeeds). Callers treat the operation as SUCCESS — never a
  // failure surface, never a replay.
  const convergeAfterApplied = useCallback(async (expectedLoanId?: string, expectedGeneration = loanGenerationRef.current): Promise<boolean> => {
    // Round-10 #1: a mutation that belonged to a loan the hook has LEFT must
    // not pre-set needs-reload on the new loan or bump the open sequence out
    // from under its in-flight open — report converged and do nothing.
    if (expectedGeneration !== loanGenerationRef.current || (expectedLoanId && loanIdRef.current !== expectedLoanId)) {
      return true;
    }
    // Round-6 #4: mark reload-required BEFORE convergence starts — a
    // superseded refetch then leaves the flag standing unless the newer
    // request itself succeeds (refetch/doOpen clear it only on OWN success).
    setNeedsReload(true);
    const id = expectedLoanId ?? aggRef.current?.order?.loan_id ?? loanId;
    const out = id ? await refetchAndReconcile(id) : 'failed';
    if (expectedGeneration !== loanGenerationRef.current || (expectedLoanId && loanIdRef.current !== expectedLoanId)) {
      return true;
    }
    return out === 'ok' || !needsReloadRef.current;
  }, [refetchAndReconcile, loanId, setNeedsReload]);

  // ---- autosave engine -----------------------------------------------------
  // Drains dirty sections/fees one at a time. A recoverable failure (409 conflict
  // or a transient resync) NEVER discards the user's edit: the popped entry is
  // re-queued (a newer edit made meanwhile still wins), we refetch+reconcile to
  // pick up the fresh revision while preserving all dirty, and retry. Only an
  // explicit server rejection (422 'soft') or a success ('ok') removes an entry.
  const processPending = useCallback((): Promise<DrainResult> => {
    const generation = loanGenerationRef.current;
    const drainLoanId = loanIdRef.current;
    const existing = flushInFlight.current;
    if (existing?.generation === generation) {
      return existing.promise;
    }
    const token = Symbol('doc-order-drain');
    const stillCurrent = () => generation === loanGenerationRef.current && drainLoanId === loanIdRef.current;
    const MAX_RETRIES = 4;
    // Install a deferred promise in the slot BEFORE starting the async body.
    // The body still begins synchronously (preserving explicit-flush timing),
    // while a zero-dirty drain can no longer clear the slot before assignment
    // and leave a completed promise absorbing every later autosave.
    let resolveRun: (value: DrainResult | PromiseLike<DrainResult>) => void = () => {};
    let rejectRun: (reason?: unknown) => void = () => {};
    const run = new Promise<DrainResult>((resolve, reject) => {
      resolveRun = resolve;
      rejectRun = reject;
    });
    flushInFlight.current = { generation, token, promise: run };
    void (async (): Promise<DrainResult> => {
      if (stillCurrent()) {
        setSaving(true);
        setSaveStalled(false); // a fresh drain attempt clears any prior stall
      }
      let retries = 0;
      let rejected = false;
      let stalled = false;
      let convergeFailed = false;
      try {
        while (dirtySections.current.size > 0 || dirtyFees.current.size > 0) {
          if (!stillCurrent()) {
            return { drained: false, rejected: false, stalled: false, convergeFailed: false };
          }
          const order = aggRef.current?.order;
          if (!order || (drainLoanId && order.loan_id !== drainLoanId)) {
	break;
}
          // Pop one entry, remembering it so a conflict can re-queue it.
          let popped: { section: SaveDraftSection } | { feeId: string; patch: Partial<DocOrderFee> };
          let res: DocOrderResponse;
          if (dirtySections.current.size > 0) {
            const section = dirtySections.current.values().next().value as SaveDraftSection;
            dirtySections.current.delete(section);
            popped = { section };
            res = await docOrderService.saveSection(order.id, order.revision, section, buildSectionPatch(section, order));
          } else {
            const [feeId, patch] = dirtyFees.current.entries().next().value as [string, Partial<DocOrderFee>];
            dirtyFees.current.delete(feeId);
            popped = { feeId, patch };
            res = await docOrderService.saveFee(order.id, order.revision, feeId, patch);
          }
          if (!stillCurrent() || loanIdRef.current !== (order.loan_id as string)) {
            // Round-11 #1: the loan changed while this save was in flight —
            // the response belongs to a LEFT loan. Apply nothing, requeue
            // nothing (the loan-change effect owns the new loan's queues),
            // surface nothing; just stop.
            return { drained: false, rejected: false, stalled: false, convergeFailed: false };
          }
          const outcome = applyResponse(res, 'section' in popped ? 'section' : 'fee');
          if (outcome === 'ok') {
            retries = 0; // reconcile already applied the fresh server state
            continue;
          }
          if (outcome === 'applied') {
            // Round-5 #1: the save PERSISTED (degraded response) — converge
            // dirty-preserving and keep draining on the refreshed revision.
            // The popped edit is NOT requeued (a replay would duplicate
            // events/revision bumps). Round-6 #1: if convergence FAILED the
            // local revision is stale — STOP draining (remaining dirty
            // entries stay queued) and fail the flush; needsReload owns the
            // messaging and blocks transitions.
            retries = 0;
            if (await convergeAfterApplied(order.loan_id, generation)) {
              if (!stillCurrent()) {
                return { drained: false, rejected: false, stalled: false, convergeFailed: false };
              }
              continue;
            }
            convergeFailed = true;
            break;
          }
          if (outcome === 'hard') {
            // R4 #3: requeue the popped entry, STOP the drain, keep the
            // optimistic edit on screen. applyResponse already toasted the
            // server message; the stall banner + flush() offer the retry.
            if ('section' in popped) {
              dirtySections.current.add(popped.section);
            } else {
              dirtyFees.current.set(popped.feeId, { ...popped.patch, ...(dirtyFees.current.get(popped.feeId) ?? {}) });
            }
            stalled = true;
            setSaveStalled(true);
            break;
          }
          if (outcome === 'soft') {
            rejected = true; // a decision gate must not read this drain as clean
            // 422: the server REJECTED this entry (already popped/dropped) —
            // server truth must REPLACE the rejected optimistic value.
            // Round-8 #1: if that truth-refetch itself fails, the rejected
            // value would sit on screen with NO banner and NO flag (a false
            // server-accepted state) — so the refetch runs through the same
            // pre-set/clear converge as applied saves: failure leaves
            // needsReload standing (visible block + Reload recovery; no
            // deadlock — retryReload is not flush-gated). The pre-R5 "cosmetic
            // residual" stance predates needsReload existing.
            retries = 0;
            if (await convergeAfterApplied(order.loan_id, generation)) {
              if (!stillCurrent()) {
                return { drained: false, rejected: false, stalled: false, convergeFailed: false };
              }
              continue;
            }
            convergeFailed = true;
            break;
          }
          // conflict (409) or resync (transient/unknown): re-queue the popped
          // edit — merging so any newer edit to the same target still wins.
          if ('section' in popped) {
            dirtySections.current.add(popped.section); // rebuilds from preserved optimistic order state
          } else {
            dirtyFees.current.set(popped.feeId, { ...popped.patch, ...(dirtyFees.current.get(popped.feeId) ?? {}) });
          }
          if (++retries > MAX_RETRIES) {
            // Give up retrying, but KEEP the dirty state — never reload/clear it
            // (that would hide the very edits we're protecting). A persistent
            // banner + the next edit / manual save re-triggers the drain.
            stalled = true;
            setSaveStalled(true);
            addToastRef.current('Unsaved changes — could not reach the server. Retry to save.', 'alertTriangle');
            break;
          }
          await refetchAndReconcile(order.loan_id); // fresh revision, dirty preserved
          if (!stillCurrent()) {
            return { drained: false, rejected: false, stalled: false, convergeFailed: false };
          }
        }
        // Fully drained iff nothing is left queued (exhaustion leaves entries).
        return {
          drained: dirtySections.current.size === 0 && dirtyFees.current.size === 0,
          rejected,
          stalled,
          convergeFailed
        };
      } finally {
        if (flushInFlight.current?.token === token) {
          flushInFlight.current = null;
          if (stillCurrent()) {
            setSaving(false);
          }
        }
      }
    })().then(resolveRun, rejectRun);
    return run;
  }, [applyResponse, refetchAndReconcile, convergeAfterApplied]);

  const scheduleFlush = useCallback(() => {
    if (debounceTimer.current) {
clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      void processPending();
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [processPending]);

  // R5 (review-5 #1): the PUBLIC boolean is strict — drained AND clean. A
  // soft-422 refetch restores server truth, so the NEXT attempt drains clean;
  // the caller's abort makes the user acknowledge the rejection first.
  const flush = useCallback(async (): Promise<boolean> => {
    // Round-10 #4: while reload-required, a public flush would send retained
    // edits at a provably stale revision — refuse OFF-WIRE. retryReload owns
    // the post-convergence drain (it calls processPending directly).
    if (needsReloadRef.current) {
      return false;
    }
    if (debounceTimer.current) {
clearTimeout(debounceTimer.current);
}
    const r = await processPending();
    return r.drained && !r.rejected && !r.stalled && !r.convergeFailed;
  }, [processPending]);

  // Awaited single-fee decision save (R5 #7b). Patch FACTORY evaluated from
  // the CURRENT fee post-flush; re-evaluated after a conflict refetch. Never
  // proceeds on a dirty/rejected drain; single-flight.
  const saveFeeNow = useCallback(
    async (
      feeId: string,
      buildPatch: (fee: DocOrderFee) => Partial<DocOrderFee>
    ): Promise<{ ok: boolean; status?: number; code?: string; error?: string }> => {
      const generation = loanGenerationRef.current;
      const entryLoanId = loanIdRef.current;
      const entryOrderId = aggRef.current?.order?.id;
      const stillCurrent = () =>
        generation === loanGenerationRef.current &&
        entryLoanId === loanIdRef.current &&
        entryOrderId === aggRef.current?.order?.id;
      if (needsReloadRef.current) {
        return { ok: false, status: 0, error: 'Reload required — the workspace is stale; press Reload and retry.' };
      }
      if (feeNowInFlight.current === generation) {
        return { ok: false, status: 0, error: 'A decision is already saving' };
      }
      feeNowInFlight.current = generation;
      try {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
        const drain = await processPending();
        if (!stillCurrent()) {
          return { ok: false, status: 0, error: 'The order changed while the decision was saving.' };
        }
        if (!drain.drained || drain.rejected || drain.stalled || drain.convergeFailed) {
          return { ok: false, status: 0, error: 'Unsaved changes must save cleanly first — fix the flagged edit and retry.' };
        }
        const attempt = async (): Promise<{ ok: boolean; status?: number; code?: string; error?: string }> => {
          const order = aggRef.current?.order;
          const fee = aggRef.current?.fees.find((f) => f.id === feeId);
          if (!order || !fee) {
            return { ok: false, status: 0, error: 'Fee is no longer on this order' };
          }
          const res = await docOrderService.saveFee(order.id, order.revision, feeId, buildPatch(fee));
          if (!stillCurrent()) {
            return { ok: false, status: 0, error: 'The order changed while the decision was saving.' };
          }
          if (res.ok && res.aggregate) {
            reconcile(res.aggregate);
            return { ok: true };
          }
          if (appliedUnconverged(res)) {
            // Round-4 #2 / round-5: the decision PERSISTED — converge
            // centrally and report success; the sheet must not re-run it.
            await convergeAfterApplied(order.loan_id, generation);
            if (!stillCurrent()) {
              return { ok: false, status: 0, error: 'The order changed while the decision was saving.' };
            }
            return { ok: true };
          }
          return { ok: false, status: res.status, code: res.code, error: res.error };
        };
        let out = await attempt();
        if (!out.ok && out.status === 409 && out.code === 'revision_conflict') {
          if (!stillCurrent() || !entryLoanId) {
            return { ok: false, status: 0, error: 'The order changed while the decision was saving.' };
          }
          await refetchAndReconcile(entryLoanId);
          if (!stillCurrent()) {
            return { ok: false, status: 0, error: 'The order changed while the decision was saving.' };
          }
          out = await attempt(); // factory re-evaluates from the fresh fee
        }
        return out;
      } finally {
        if (feeNowInFlight.current === generation) {
          feeNowInFlight.current = null;
        }
      }
    },
    [processPending, reconcile, refetchAndReconcile, convergeAfterApplied]
  );

  // Any editable change to a signed DRAFT clears the signature immediately
  // (matches the server's material-edit staleness cascade — README §8.8).
  // DRAFT ONLY (R4): a closer's submitted-stage closing/vesting save must not
  // optimistically wipe the processor signature the server preserves.
  const optimisticSignClear = (order: DocOrderRow): Partial<DocOrderRow> =>
    order.stage === 'draft' && order.signed_at ? { signed_at: null, signed_by: null } : {};

  const mutateOrder = useCallback(
    (section: SaveDraftSection, mutate: (order: DocOrderRow) => Partial<DocOrderRow>) => {
      if (needsReloadRef.current) {
        return; // round-6 #2: no optimistic edits on a provably-stale workspace
      }
      const cur = aggRef.current;
      if (!cur) {
return;
}
      const patch = { ...mutate(cur.order), ...optimisticSignClear(cur.order) };
      setAgg({ ...cur, order: { ...cur.order, ...patch } });
      dirtySections.current.add(section);
      scheduleFlush();
    },
    [setAgg, scheduleFlush]
  );

  const patchClosing = useCallback<UseDocOrderResult['patchClosing']>(
    (patch) => mutateOrder('closing', () => patch),
    [mutateOrder]
  );

  const patchSettlement = useCallback<UseDocOrderResult['patchSettlement']>(
    (patch) => mutateOrder('settlement', (order) => ({ settlement: { ...(order.settlement ?? emptySettlement()), ...patch } })),
    [mutateOrder]
  );

  const patchInsurance = useCallback<UseDocOrderResult['patchInsurance']>(
    (patch) => mutateOrder('insurance', (order) => ({ insurance: { ...(order.insurance ?? ({} as InsuranceInfo)), ...patch } })),
    [mutateOrder]
  );

  const patchVesting = useCallback<UseDocOrderResult['patchVesting']>(
    (patch) =>
      mutateOrder('vesting', (order) => ({
        vesting: patch.vesting !== undefined ? patch.vesting : order.vesting,
        vesting_workflow: patch.vesting_workflow
          ? { ...(order.vesting_workflow ?? {}), ...patch.vesting_workflow }
          : order.vesting_workflow,
        vesting_confirmed: patch.vesting_confirmed !== undefined ? patch.vesting_confirmed : order.vesting_confirmed
      })),
    [mutateOrder]
  );

  const saveFee = useCallback<UseDocOrderResult['saveFee']>(
    (feeId, patch) => {
      if (needsReloadRef.current) {
        return; // round-6 #2
      }
      const cur = aggRef.current;
      if (!cur) {
return;
}
      const signClear = optimisticSignClear(cur.order);
      setAgg({
        ...cur,
        order: { ...cur.order, ...signClear },
        fees: cur.fees.map((f) => (f.id === feeId ? { ...f, ...patch } : f))
      });
      dirtyFees.current.set(feeId, { ...(dirtyFees.current.get(feeId) ?? {}), ...patch });
      scheduleFlush();
    },
    [setAgg, scheduleFlush]
  );

  const addManualFee = useCallback<UseDocOrderResult['addManualFee']>(
    async (patch) => {
      if (needsReloadRef.current) {
        addToastRef.current('Reload required — the last change saved but the workspace is stale', 'refresh');
        return;
      }
      const generation = loanGenerationRef.current;
      const entryLoanId = loanIdRef.current;
      const entryOrderId = aggRef.current?.order?.id;
      // Don't mutate on top of unsaved edits — a 409 here would bare-reload and
      // discard the retained dirty state (Codex P0). Make the user retry first.
      const flushed = await flush();
      if (generation !== loanGenerationRef.current || loanIdRef.current !== entryLoanId || aggRef.current?.order?.id !== entryOrderId) {
        return;
      }
      if (!flushed) {
        addToastRef.current('Unsaved changes — retry the save before adding a fee', 'alertTriangle');
        return;
      }
      const order = aggRef.current?.order;
      if (!order || loanIdRef.current !== entryLoanId || order.id !== entryOrderId) {
        return;
      }
      const res = await docOrderService.addManualFee(order.id, order.revision, patch);
      if (generation !== loanGenerationRef.current || loanIdRef.current !== entryLoanId || aggRef.current?.order?.id !== entryOrderId) {
        return;
      }
      if (appliedUnconverged(res)) {
        // Round-5 #2: the fee row EXISTS (server minted its uuid) — an error
        // here would invite a retry that inserts a DUPLICATE. Converge.
        await convergeAfterApplied(order.loan_id, generation);
        return;
      }
      if (res.ok && res.aggregate) {
reconcile(res.aggregate);
} else if (res.status === 409) {
        const reopened = await doOpen(order.loan_id);
        if (reopened === 'ok') {
          addToastRef.current('Order changed — reloaded', 'refresh');
        }
      } else {
addToastRef.current(res.error || 'Could not add fee', 'alertTriangle');
}
    },
    [flush, reconcile, doOpen, convergeAfterApplied]
  );

  const createOrder = useCallback(async () => {
    if (!loanId) {
return;
}
    const generation = loanGenerationRef.current;
    setCreating(true);
    setCreateRetryable(false);
    setError(null);
    const res = await docOrderService.create(loanId);
    if (generation !== loanGenerationRef.current || loanIdRef.current !== loanId) {
      return; // round-11 #1: created for a loan the hook has left — the next
      // mount's open loads it; installing here would blank the current loan.
    }
    setCreating(false);
    if (res.ok && res.aggregate) {
      setAgg(res.aggregate);
    } else if (appliedUnconverged(res)) {
      // Round-5 #2: the order was CREATED; only the response aggregate raced.
      // Load it — a doOpen failure surfaces error + Try again (reopen), and
      // create is never replayed (a replay would 409 on the active index).
      await doOpen(loanId);
    } else if (res.status === 409) {
      await doOpen(loanId); // active order already exists — load it
    } else {
      setError(res.error || 'Could not start the doc order');
      setCreateRetryable(!!res.retryable);
    }
  }, [loanId, setAgg, doOpen]);

  const refresh = useCallback(async () => {
    if (needsReloadRef.current) {
      addToastRef.current('Reload required — the last change saved but the workspace is stale', 'refresh');
      return;
    }
    const generation = loanGenerationRef.current;
    const entryLoanId = loanIdRef.current;
    const entryOrderId = aggRef.current?.order?.id;
    // A refresh pulls fresh server facts and could clobber unsaved optimistic
    // edits; if the drain stalled, make the user resolve it first (Codex P0).
    const flushed = await flush();
    if (generation !== loanGenerationRef.current || loanIdRef.current !== entryLoanId || aggRef.current?.order?.id !== entryOrderId) {
      return;
    }
    if (!flushed) {
      addToastRef.current('Unsaved changes — retry the save before refreshing', 'alertTriangle');
      return;
    }
    const order = aggRef.current?.order;
    if (!order || loanIdRef.current !== entryLoanId || order.id !== entryOrderId) {
      return;
    }
    setRefreshing(true);
    const res = await docOrderService.refresh(order.id, order.revision);
    if (generation !== loanGenerationRef.current || loanIdRef.current !== entryLoanId || aggRef.current?.order?.id !== entryOrderId) {
      return;
    }
    setRefreshing(false);
    if (res.ok && res.aggregate) {
reconcile(res.aggregate);
} else if (appliedUnconverged(res)) {
      await convergeAfterApplied(order.loan_id, generation);
} else if (res.status === 409) {
      const reopened = await doOpen(order.loan_id);
      if (reopened === 'ok') {
        addToastRef.current('Order changed — reloaded', 'refresh');
      }
    } else {
addToastRef.current(res.error || 'Refresh failed', 'alertTriangle');
}
  }, [flush, reconcile, doOpen, convergeAfterApplied]);

  // Round-6 #2b: the banner retry must PRESERVE queued optimistic edits —
  // a bare doOpen would replace the aggregate under still-dirty section keys
  // and erase their values before the flush rebuilds patches from state.
  // Round-7 #1: converging is only HALF the recovery — the drain that hit
  // the failed convergence stopped with entries still queued and no timer
  // pending, so Reload must also DRAIN them; otherwise they sit silently
  // unsaved (no banner, no debounce) and navigation would discard them. A
  // drain failure re-raises its own state (stall / reject / a fresh
  // needs-reload), so nothing ends silent.
  const retryReload = useCallback(async () => {
    const generation = loanGenerationRef.current;
    const retryLoanId = loanIdRef.current;
    const converged = await convergeAfterApplied(retryLoanId ?? undefined, generation);
    if (generation !== loanGenerationRef.current || retryLoanId !== loanIdRef.current) {
      return;
    }
    if (!converged) {
      return; // still blocked — the banner stands
    }
    if (dirtySections.current.size > 0 || dirtyFees.current.size > 0) {
      await processPending();
    }
  }, [convergeAfterApplied, processPending]);

  const reopen = useCallback(async () => {
    // Round-4 #1: the loan PROP is the truth — a stale aggregate from a
    // previous loan must never steer the retry.
    if (loanId) {
      await doOpen(loanId);
    } else if (aggRef.current?.order) {
      await doOpen(aggRef.current.order.loan_id);
    }
  }, [doOpen, loanId]);

  const loadLoSummary = useCallback(async (): Promise<boolean> => {
    const generation = loanGenerationRef.current;
    const order = aggRef.current?.order;
    if (!order) {
      return false;
    }
    const key = `${order.loan_id}:${order.id}:${order.revision}`;
    const cached = loSummaryRef.current;
    if (cached && `${order.loan_id}:${cached.orderId}:${cached.revision}` === key) {
      return true;
    }
    if (loSummaryInFlight.current?.key === key) {
      return loSummaryInFlight.current.promise;
    }

    setLoSummaryLoading(true);
    setLoSummaryError(null);
    const promise = (async () => {
      const res = await docOrderService.loadLoSummary(order.id, order.revision);
      if (
        generation !== loanGenerationRef.current ||
        aggRef.current?.order?.loan_id !== order.loan_id ||
        aggRef.current?.order?.id !== order.id ||
        aggRef.current?.order?.revision !== order.revision
      ) {
        return false;
      }
      if (res.ok && res.loSummary && res.loSummary.orderId === order.id && res.loSummary.revision === order.revision) {
        loSummaryRef.current = res.loSummary;
        setLoSummaryState(res.loSummary);
        return true;
      }
      if (res.status === 409) {
        await refetchAndReconcile(order.loan_id);
      } else {
        setLoSummaryError(res.error || 'Could not load lender-credit and compensation details');
      }
      return false;
    })().finally(() => {
      let ownedCurrentRequest = false;
      if (loSummaryInFlight.current?.key === key) {
        loSummaryInFlight.current = null;
        ownedCurrentRequest = true;
      }
      if (ownedCurrentRequest && generation === loanGenerationRef.current) {
        setLoSummaryLoading(false);
      }
    });
    loSummaryInFlight.current = { key, promise };
    return promise;
  }, [refetchAndReconcile]);

  // Check status (Batch 3.1 F4): lightweight upload-queue convergence via the
  // dedicated `check-uploads` action — deliberately NOT `refresh` (a fact pull
  // that bumps the revision = the #14 churn under an active upload queue).
  // reconcile() preserves dirty edits; the openSeq + order-id capture ignores
  // a late response after a loan switch / re-open (stale-response rule).
  const checkUploadStatus = useCallback(async () => {
    if (needsReloadRef.current) {
      addToastRef.current('Reload required — the last change saved but the workspace is stale', 'refresh');
      return;
    }
    if (checkingUploadsRef.current) {
return;
} // single shared in-flight check
    const generation = loanGenerationRef.current;
    const order = aggRef.current?.order;
    if (!order) {
return;
}
    // Capture the token SYNCHRONOUSLY at entry — a re-open that starts while
    // the flush below is awaited bumps openSeq, and this check must lose to it
    // (capturing after the await would share the re-open's own token).
    const seq = openSeq.current;
    const orderId = order.id;
    // Claim the single-flight slot BEFORE any await (Codex 3.1 blocker): two
    // rapid clicks must not both pass the guard during the flush window.
    checkingUploadsRef.current = true;
    setCheckingUploads(true);
    try {
      const flushed = await flush();
      if (generation !== loanGenerationRef.current || aggRef.current?.order?.id !== orderId) {
        return;
      }
      if (!flushed) {
        addToastRef.current('Unsaved changes — retry the save before checking', 'alertTriangle');
        return;
      }
      if (seq !== openSeq.current) {
        return; // a newer same-order open won while the clean flush was pending
      }
      const res = await docOrderService.checkUploads(orderId);
      if (generation !== loanGenerationRef.current || seq !== openSeq.current || aggRef.current?.order?.id !== orderId) {
        return; // stale — a newer open/loan owns the state now
      }
      if (res.ok && res.aggregate) {
        // Same-order staleness (Codex 3.1 blocker): refresh()/fee saves bump
        // the REVISION without touching openSeq — a check that read the DB
        // before such a write must never roll the UI back to its older
        // aggregate. (Convergence itself never bumps the revision, so an
        // equal revision is current, not stale.)
        const currentRev = aggRef.current?.order?.revision ?? 0;
        if (res.aggregate.order.revision < currentRev) {
          return;
        }
        reconcile(res.aggregate);
      } else {
        addToastRef.current(res.error || 'Could not check upload status — try again', 'alertTriangle');
      }
    } finally {
      if (generation === loanGenerationRef.current) {
        checkingUploadsRef.current = false;
        setCheckingUploads(false);
      }
    }
  }, [flush, reconcile]);

  const sign = useCallback(async (): Promise<{ ok: boolean; blockers?: string[]; error?: string }> => {
    // flush() is a HARD precondition — signing on top of unsaved edits would run
    // a stale revision and the 409 branch would bare-reload, wiping the retained
    // optimistic edits (Codex P0). Abort until the drain succeeds.
    if (needsReloadRef.current) {
      addToastRef.current('Reload required — the last change saved but the workspace is stale', 'refresh');
      return { ok: false };
    }
    const generation = loanGenerationRef.current;
    const entryLoanId = loanIdRef.current;
    const entryOrderId = aggRef.current?.order?.id;
    const flushed = await flush();
    if (generation !== loanGenerationRef.current || loanIdRef.current !== entryLoanId || aggRef.current?.order?.id !== entryOrderId) {
      return { ok: false };
    }
    if (!flushed) {
      addToastRef.current('Unsaved changes — retry the save before signing', 'alertTriangle');
      return { ok: false };
    }
    const order = aggRef.current?.order;
    if (!order || loanIdRef.current !== entryLoanId || order.id !== entryOrderId) {
      return { ok: false };
    }
    setSigning(true);
    const res = await docOrderService.sign(order.id, order.revision);
    if (generation !== loanGenerationRef.current || loanIdRef.current !== entryLoanId || aggRef.current?.order?.id !== entryOrderId) {
      return { ok: false };
    }
    setSigning(false);
    if (appliedUnconverged(res)) {
      // Round-5 #2: the signature PERSISTED — converge and report SUCCESS
      // (the previous shape fell through to ok:false after converging).
      await convergeAfterApplied(order.loan_id, generation);
      if (generation !== loanGenerationRef.current || loanIdRef.current !== entryLoanId || aggRef.current?.order?.id !== entryOrderId) {
        return { ok: false };
      }
      return { ok: true };
    }
    if (res.ok && res.aggregate) {
      reconcile(res.aggregate);
      return { ok: true };
    }
    if (res.status === 409) {
      const reopened = await doOpen(order.loan_id);
      if (reopened === 'ok') {
        addToastRef.current('Order changed — reloaded', 'refresh');
      }
      return { ok: false };
    }
    // R4 rev-2 (#3): a 422 without a blockers[] (e.g. the POA/Hybrid gate)
    // must reach the UI — dropping res.error made the server's own safeguard
    // fail silently at the sign pad.
    return { ok: false, blockers: res.blockers, error: res.error };
  }, [flush, reconcile, doOpen, convergeAfterApplied]);

  // ---- P2+P3 workflow transitions ------------------------------------------
  // Generic runner: flush pending draft edits, carry the current revision, then
  // reconcile the server aggregate on success. 409 revision conflicts reload;
  // every other failure is left to the caller unless an errorToast is supplied
  // (mirror_stale / writeback_disabled surface res.error verbatim through it).
  const runAction = useCallback(
    async (
      call: (orderId: string, revision: number) => Promise<DocOrderResponse>,
      opts: { flushFirst?: boolean; errorToast?: string } = {}
    ): Promise<DocOrderResponse> => {
      if (needsReloadRef.current) {
        addToastRef.current('Reload required — the last change saved but the workspace is stale', 'refresh');
        return { ok: false, status: 0, aggregate: null, viewer: null };
      }
      // Round-10 #1 (sharpened by its own regression): identity is captured at
      // ENTRY — the preliminary flush parks on an await, and a loan switch in
      // that window would otherwise fire the user's action against the NEW
      // loan's order. If the world moved while we flushed, abort silently.
      const generation = loanGenerationRef.current;
      const entryLoanId = loanIdRef.current;
      const entryOrderId = aggRef.current?.order?.id;
      if (opts.flushFirst !== false) {
        // Hard precondition: never run a workflow transition on top of unsaved
        // edits — a stale-revision 409 would bare-reload and wipe them (Codex P0).
        const flushed = await flush();
        if (generation !== loanGenerationRef.current || loanIdRef.current !== entryLoanId || aggRef.current?.order?.id !== entryOrderId) {
          return { ok: false, status: 0, aggregate: null, viewer: null };
        }
        if (!flushed) {
          addToastRef.current('Unsaved changes — retry the save first', 'alertTriangle');
          return { ok: false, status: 0, aggregate: null, viewer: null };
        }
      }
      const order = aggRef.current?.order;
      if (!order || loanIdRef.current !== entryLoanId || order.id !== entryOrderId) {
        return { ok: false, status: 0, aggregate: null, viewer: null };
      }
      setBusy(true);
      try {
        const res = await call(order.id, order.revision);
        if (generation !== loanGenerationRef.current || loanIdRef.current !== entryLoanId || aggRef.current?.order?.id !== entryOrderId) {
          return { ok: false, status: 0, aggregate: null, viewer: null };
        }
        if (res.ok && res.aggregate) {
          reconcile(res.aggregate);
        } else if (appliedUnconverged(res)) {
          // Round-4 #2 / round-5: applied — converge centrally (a failed
          // converge flips needsReload); NEVER failure, NEVER a replay.
          await convergeAfterApplied(order.loan_id, generation);
          if (generation !== loanGenerationRef.current || loanIdRef.current !== entryLoanId || aggRef.current?.order?.id !== entryOrderId) {
            return { ok: false, status: 0, aggregate: null, viewer: null };
          }
        } else if (res.status === 409 && res.code === 'revision_conflict') {
          const reopened = await doOpen(order.loan_id);
          if (generation !== loanGenerationRef.current || loanIdRef.current !== entryLoanId || aggRef.current?.order?.id !== entryOrderId) {
            return { ok: false, status: 0, aggregate: null, viewer: null };
          }
          if (reopened === 'ok') {
            addToastRef.current('Order changed — reloaded', 'refresh');
          }
        } else if (opts.errorToast !== undefined) {
          addToastRef.current(res.error || opts.errorToast, 'alertTriangle');
        }
        return res;
      } finally {
        if (generation === loanGenerationRef.current) {
          setBusy(false);
        }
      }
    },
    [flush, reconcile, doOpen, convergeAfterApplied]
  );

  const deferInsurance = useCallback<UseDocOrderResult['deferInsurance']>(
    (reason) => runAction((id, rev) => docOrderService.deferInsurance(id, rev, reason), { errorToast: 'Could not defer insurance' }),
    [runAction]
  );
  const submit = useCallback<UseDocOrderResult['submit']>(
    () => runAction((id, rev) => docOrderService.submit(id, rev), { errorToast: 'Submit failed' }),
    [runAction]
  );
  const reopenOrder = useCallback<UseDocOrderResult['reopenOrder']>(
    (reason) => runAction((id, rev) => docOrderService.reopenDocOrder(id, rev, reason), { errorToast: 'Could not reopen the order' }),
    [runAction]
  );
  const returnToProcessor = useCallback<UseDocOrderResult['returnToProcessor']>(
    () => runAction((id, rev) => docOrderService.returnToProcessor(id, rev), { errorToast: 'Could not return to the processor' }),
    [runAction]
  );
  // Round-7 #2: NO pre-optimism on the closer actions — runAction can refuse
  // (needs-reload, dirty flush) or the server can reject (403/422), and a
  // locally-edited/approved fee with no persistence is exactly the
  // false-success class R5 exists to kill. Success renders from the
  // reconciled server aggregate; failure leaves the row untouched.
  const closingEditFee = useCallback<UseDocOrderResult['closingEditFee']>(
    (feeId, patch) =>
      runAction((id, rev) => docOrderService.closingEditFee(id, rev, feeId, patch), { errorToast: 'That edit was rejected' }),
    [runAction]
  );
  const approveFee = useCallback<UseDocOrderResult['approveFee']>(
    (feeId, approve) => runAction((id, rev) => docOrderService.approveFee(id, rev, feeId, approve), { errorToast: 'Approval failed' }),
    [runAction]
  );
  const vestingAttest = useCallback<UseDocOrderResult['vestingAttest']>(
    () => runAction((id, rev) => docOrderService.vestingAttest(id, rev), { errorToast: 'Could not record the attestation' }),
    [runAction]
  );
  const resolveChangeRequest = useCallback<UseDocOrderResult['resolveChangeRequest']>(
    () => runAction((id, rev) => docOrderService.resolveChangeRequest(id, rev), { errorToast: 'Could not clear the change request' }),
    [runAction]
  );
  const sendToLo = useCallback<UseDocOrderResult['sendToLo']>(
    () => runAction((id, rev) => docOrderService.sendToLo(id, rev), { errorToast: 'Send failed' }),
    [runAction]
  );
  // set/escalate: the caller (LO summary) branches on over_capacity / floor.
  const setLenderCredit = useCallback<UseDocOrderResult['setLenderCredit']>(
    (amount) => runAction((id, rev) => docOrderService.setLenderCredit(id, rev, amount)),
    [runAction]
  );
  const escalateLenderCredit = useCallback<UseDocOrderResult['escalateLenderCredit']>(
    (amount) => runAction((id, rev) => docOrderService.escalateLenderCredit(id, rev, amount), { errorToast: 'Escalation failed' }),
    [runAction]
  );
  const withdrawLenderCreditEscalation = useCallback<UseDocOrderResult['withdrawLenderCreditEscalation']>(
    () => runAction((id, rev) => docOrderService.withdrawLenderCreditEscalation(id, rev), { errorToast: 'Could not withdraw escalation' }),
    [runAction]
  );
  const loRequestChanges = useCallback<UseDocOrderResult['loRequestChanges']>(
    (note, items) => runAction((id, rev) => docOrderService.loRequestChanges(id, rev, note, items), { errorToast: 'Could not send changes' }),
    [runAction]
  );
  // acknowledge: the caller inspects res.pendingWrite (unconverged 4794 write).
  const loAcknowledge = useCallback<UseDocOrderResult['loAcknowledge']>(
    () => runAction((id, rev) => docOrderService.loAcknowledge(id, rev), { errorToast: 'Could not acknowledge' }),
    [runAction]
  );
  const retryWrites = useCallback<UseDocOrderResult['retryWrites']>(
    () => runAction((id, rev) => docOrderService.retryWrites(id, rev), { errorToast: 'Retry failed' }),
    [runAction]
  );
  // Upload runs through runAction (flush + busy + 409 reload + aggregate reconcile).
  // No errorToast: the UploadPanel owns every non-409 message (reject banner,
  // extraction-unavailable retry, disabled gate) so it can branch on res.upload /
  // res.code — a generic toast here would double up on the panel's inline copy.
  const uploadFile = useCallback<UseDocOrderResult['uploadFile']>(
    (target, file, fileName) =>
      runAction((id, rev) => docOrderService.uploadDocOrderFile({ orderId: id, expectedRevision: rev, file, fileName, target })),
    [runAction]
  );
  const commitMarks = useCallback<UseDocOrderResult['commitMarks']>(
    (feeId) => runAction((id, rev) => docOrderService.commitMarks(id, rev, feeId), { errorToast: 'Could not commit invoice marks' }),
    [runAction]
  );
  // Escalations are server-owned actions (R5): the sheet owns the messaging,
  // so no generic errorToast here.
  const escalateFee = useCallback<UseDocOrderResult['escalateFee']>(
    (feeId, note) => runAction((id, rev) => docOrderService.escalateFee(id, rev, feeId, note)),
    [runAction]
  );
  const undoEscalation = useCallback<UseDocOrderResult['undoEscalation']>(
    (feeId) => runAction((id, rev) => docOrderService.undoEscalation(id, rev, feeId)),
    [runAction]
  );
  const setFirstPaymentOverride = useCallback<UseDocOrderResult['setFirstPaymentOverride']>(
    (input) => runAction((id, rev) => docOrderService.setFirstPaymentOverride(id, rev, input)),
    [runAction]
  );
  const resetDocument = useCallback<UseDocOrderResult['resetDocument']>(
    (checklistId) => runAction((id, rev) => docOrderService.resetDocument(id, rev, checklistId), { errorToast: 'Could not reopen the document' }),
    [runAction]
  );
  const deleteFee = useCallback<UseDocOrderResult['deleteFee']>(
    (feeId) => runAction((id, rev) => docOrderService.deleteFee(id, rev, feeId), { errorToast: 'Could not delete the fee' }),
    [runAction]
  );
  const resendEmail = useCallback<UseDocOrderResult['resendEmail']>(
    (claimId) => runAction((id, rev) => docOrderService.resendEmail(id, rev, claimId), { errorToast: 'Resend failed' }),
    [runAction]
  );
  const downloadPdf = useCallback<UseDocOrderResult['downloadPdf']>(async () => {
    // Round-11 #1: identity at ENTRY — a loan switch during the preliminary
    // flush must not download the NEW loan's PDF for the old click.
    const generation = loanGenerationRef.current;
    const entryLoanId = loanIdRef.current;
    const entryOrderId = aggRef.current?.order?.id;
    // Abort on a stalled drain so the PDF can't be built from a stale server
    // state that's missing the user's unsaved edits (Codex P0).
    const flushed = await flush();
    if (generation !== loanGenerationRef.current || loanIdRef.current !== entryLoanId || aggRef.current?.order?.id !== entryOrderId) {
      return;
    }
    if (!flushed) {
      addToastRef.current('Unsaved changes — retry the save before downloading', 'alertTriangle');
      return;
    }
    const order = aggRef.current?.order;
    if (!order || loanIdRef.current !== entryLoanId || order.id !== entryOrderId) {
      return;
    }
    setBusy(true);
    try {
      const res = await docOrderService.downloadPdf(order.id, order.revision);
      if (generation !== loanGenerationRef.current || loanIdRef.current !== entryLoanId || aggRef.current?.order?.id !== entryOrderId) {
        return;
      }
      if (res.ok && res.pdfBase64 && res.filename) {
        triggerPdfDownload(res.pdfBase64, res.filename);
      } else if (res.status === 409) {
        const reopened = await doOpen(order.loan_id);
        if (reopened === 'ok') {
          addToastRef.current('Order changed — reloaded', 'refresh');
        }
      } else {
        addToastRef.current(res.error || 'Could not build the PDF', 'alertTriangle');
      }
    } finally {
      if (generation === loanGenerationRef.current) {
        setBusy(false);
      }
    }
  }, [flush, doOpen]);

  // Initial load / loan change. The token bumps in EVERY branch — disabled
  // and cleanup included — so an in-flight open can never apply after the
  // hook flips off or the loan changes (round-10 finding 2).
  useEffect(() => {
    if (!enabled || !loanId) {
      openSeq.current++;
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOpenRetryable(false);
    setCreateRetryable(false);
    setNeedsReload(false);
    setSaveStalled(false);
    setSaving(false);
    setCreating(false);
    setRefreshing(false);
    setSigning(false);
    setBusy(false);
    loSummaryRef.current = null;
    setLoSummaryState(null);
    setLoSummaryLoading(false);
    setLoSummaryError(null);
    loSummaryInFlight.current = null;
    checkingUploadsRef.current = false;
    setCheckingUploads(false);
    feeNowInFlight.current = null;
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    dirtySections.current.clear();
    dirtyFees.current.clear();
    (async () => {
      await doOpen(loanId);
      if (!cancelled) {
setLoading(false);
}
    })();
    return () => {
      cancelled = true;
      // Bumping the LATEST token value is the point — a monotonic counter,
      // not a captured DOM node, so the stale-ref warning doesn't apply here.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      openSeq.current++;
    };
  }, [loanId, enabled, doOpen, setNeedsReload]);

  // Codex R5-a round-4 finding 1: expose the aggregate ONLY when it belongs
  // to the loan this hook is currently mounted for. A failed open after a
  // loan switch must render the NEW loan's error (with its retry), never the
  // previous loan's order under the new loan's facts.
  const current = agg && loanId && (agg.order.loan_id as string) === loanId ? agg : null;
  const currentLoSummary =
    current &&
    loSummary &&
    loSummary.orderId === current.order.id &&
    loSummary.revision === current.order.revision
      ? loSummary
      : null;
  return {
    loading,
    error,
    order: current?.order ?? null,
    fees: current?.fees ?? [],
    derivedDocs: current?.derivedDocs ?? [],
    invoiceFolderStatus: current?.invoiceFolderStatus ?? {},
    invoiceFolderDocs: current?.invoiceFolderDocs ?? {},
    viewer,
    snapshotStale: current?.snapshotStale ?? false,
    hasOrder: !!current,
    creating,
    createRetryable,
    openRetryable,
    needsReload,
    retryReload,
    refreshing,
    signing,
    saving,
    saveStalled,
    busy,
    capacity: currentLoSummary?.capacity ?? current?.capacity ?? null,
    loProfile: currentLoSummary?.loProfile ?? current?.loProfile ?? null,
    loSummaryLoading,
    loSummaryError,
    loadLoSummary,
    emailClaims: current?.emailClaims ?? [],
    writebacks: current?.writebacks ?? EMPTY_WRITEBACKS,
    createOrder,
    refresh,
    reopen,
    checkUploadStatus,
    checkingUploads,
    sign,
    flush,
    saveFeeNow,
    escalateFee,
    undoEscalation,
    setFirstPaymentOverride,
    patchClosing,
    patchSettlement,
    patchInsurance,
    patchVesting,
    saveFee,
    addManualFee,
    deferInsurance,
    submit,
    returnToProcessor,
    reopenOrder,
    closingEditFee,
    approveFee,
    vestingAttest,
    resolveChangeRequest,
    sendToLo,
    setLenderCredit,
    escalateLenderCredit,
    withdrawLenderCreditEscalation,
    loRequestChanges,
    loAcknowledge,
    retryWrites,
    resendEmail,
    downloadPdf,
    uploadFile,
    commitMarks,
    resetDocument,
    deleteFee
  };
}

/** Trigger a browser download for a base64 PDF (download-pdf action result). */
function triggerPdfDownload(base64: string, filename: string): void {
  try {
    const bytes = atob(base64);
    const buf = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) {
      buf[i] = bytes.charCodeAt(i);
    }
    const blob = new Blob([buf], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    // A malformed base64 shouldn't crash the acknowledgement sheet.
  }
}

function emptySettlement(): DocOrderSettlement {
  return { agent: null, contact: null, phone: null, email: null, ccRecipients: [] };
}

function buildSectionPatch(section: SaveDraftSection, order: DocOrderRow): Record<string, unknown> {
  switch (section) {
    case 'closing': {
      const patch: Record<string, unknown> = {};
      for (const col of CLOSING_COLUMNS) {
patch[col] = order[col];
}
      return patch;
    }
    case 'settlement':
      return { settlement: order.settlement };
    case 'insurance':
      return { insurance: order.insurance };
    case 'vesting':
      return {
        vesting: order.vesting,
        vesting_workflow: order.vesting_workflow,
        vesting_confirmed: order.vesting_confirmed
      };
    case 'checklist':
      return { checklist: order.checklist };
  }
}

function applyLocalSection(target: DocOrderRow, local: DocOrderRow, section: SaveDraftSection): DocOrderRow {
  switch (section) {
    case 'closing': {
      const next = { ...target };
      for (const col of CLOSING_COLUMNS) {
(next as Record<string, unknown>)[col] = local[col];
}
      return next;
    }
    case 'settlement':
      return { ...target, settlement: local.settlement };
    case 'insurance':
      return { ...target, insurance: local.insurance };
    case 'vesting':
      return {
        ...target,
        vesting: local.vesting,
        vesting_workflow: local.vesting_workflow,
        vesting_confirmed: local.vesting_confirmed
      };
    case 'checklist':
      return { ...target, checklist: local.checklist };
  }
}
